<template>
  <div class="main-content">
    <div class="section__content section__content--p30">
      <div class="container-fluid create-job-container p-b-40">
        <v-card class="p-b-30 p-t-30 p-l-20">
          <div class="el-loading-mask" v-if="loading">
            <div class="el-loading-spinner">
              <svg viewBox="25 25 50 50" class="circular">
                <circle cx="50" cy="50" r="20" fill="none" class="path"></circle>
              </svg>
            </div>
          </div>
          <!-- Card Title -->
          <div class="row">
            <div class="btn-actions-pane-left">
              <h3 class="title-1 p-l-140">
                <v-btn
                  text
                  icon
                  class="m-0"
                  @click="$router.back()">
                  <i class="zmdi zmdi-long-arrow-left"></i>
                </v-btn>
                {{ text('new_record_header') }}
              </h3>
            </div>
          </div>
          <!-- Name and Profile -->
          <div class="section">
            <div class="row p-b-10">
              <div class="col-md-2 col-l"></div>
              <div class="col-md-10 section-description">
                {{ text('record_name_text') }}
              </div>
            </div>
            <div class="row">
              <div class="col-md-2 col-l"></div>
              <div class="col-md-10">
                <v-text-field
                  prepend-icon="fas fa-sticky-note"
                  v-model="form.name"
                  :label="text('title_label')"
                  :counter="255"
                  :rules="rules.nameRules"
                  required>
                </v-text-field>
              </div>
            </div>
          </div>
          <!-- Channels -->
          <div class="section">
            <div class="row p-b-10">
              <div class="col-md-2 col-l">
                <h2>{{ text('channel') }}</h2>
              </div>
              <div class="col-md-10 section-description">
                {{ text('record_channel_text') }}
              </div>
            </div>
            <div class="row">
              <div class="col-md-2 col-l"></div>
              <!--div class="col-md-3 p-r-0 whitelist-section">
                <v-radio-group v-model="form.sourceType">
                  <v-radio label="Select Channel" value="channel"></v-radio>
                  <v-radio label="New Source" value="newSource"></v-radio>
                </v-radio-group>
              </div-->
              <div class="col-md-10" v-if="form.sourceType === 'channel'">
                <v-select
                  prepend-icon="fas fa-list-alt"
                  v-model="form.channel"
                  :label="text('channel_label')"
                  :items="channels"
                  @change="channelSelected"
                  return-object
                  item-text="name">
                </v-select>
                <v-select
                  v-if="urls.length > 0"
                  v-model="form.channelSource"
                  label="HLS Source*"
                  :append-icon="form.channelSource ? 'fa-play' : ''"
                  @click:append="openPlayer(form.channelSource)"
                  :items="urls">
                </v-select>
                <v-combobox
                  v-if="form.channelSource"
                  v-model="form.bitrates"
                  :items="form.channel.bitrates"
                  label="Select bitrate(s) for recording*"
                  item-text="name"
                  item-value="index"
                  hide-details
                  multiple
                  chips>
                  <template v-slot:selection="data">
                    <v-chip
                      :key="JSON.stringify(data.item)"
                      v-bind="data.attrs"
                      :input-value="data.selected"
                      :disabled="data.disabled"
                      @click:close="data.parent.selectItem(data.item)">
                      {{ data.item.name }}
                    </v-chip>
                  </template>
                </v-combobox>
              </div>
              <div class="col-md-7" v-if="form.sourceType === 'newSource'">
                <v-text-field
                  v-model="form.newSource"
                  label="HLS Source*"
                  :rules="rules.sourceRules"
                  :append-icon="newSourceIsValid() ? 'fa-play' : ''"
                  @click:append="openPlayer(form.newSource)"
                  persistent-hint
                  hint="Example: http://transcoder.net/content/playlist.m3u8"
                  required>
                </v-text-field>
              </div>
            </div>
          </div>
          <!-- Duration -->
          <div class="section">
            <div class="row p-b-10">
              <div class="col-md-2 col-l">
                <h2>{{ text('duration') }}</h2>
              </div>
              <div class="col-md-10 section-description">
                {{ text('record_period_text') }}
              </div>
            </div>
            <div class="row">
              <div class="col-md-2 col-l"></div>
              <div class="col-md-3">
                <div class="row">
                  <div class="col-md-6">
                    <v-text-field
                      v-model="form.duration.days"
                      type="number"
                      @change="dayPeriodChanged"
                      :suffix="text('days')">
                    </v-text-field>
                  </div>
                  <div class="col-md-6">
                    <v-text-field
                      @change="hourPeriodChanged"
                      v-model="form.duration.hours"
                      type="number"
                      :suffix="text('hours')">
                    </v-text-field>
                  </div>
                </div>
                <div class="row" v-if="limit > 0 && !isPeriodValid">
                  <div class="col-md-12 error--text body-1">
                    the limit is exceeded
                  </div>
                </div>
              </div>
              <div class="col-md-3 p-l-30">
                <v-card flat>
                  <v-card-title>{{ text('your_limit') }}
                    <span class="p-l-10 p-r-10">
                      <strong>{{ limitStr }}</strong>
                    </span>
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <i class="fa fa-question-circle"
                           style="color: #c4c4cc;"
                           v-on="on"></i>
                      </template>
                      If you want to change the limit please contact us.
                    </v-tooltip>
                  </v-card-title>
                </v-card>
              </div>
            </div>
          </div>
          <!-- Action button -->
          <div class="row text-right p-t-10">
            <div class="col-md-2 col-l"></div>
            <div class="col-md-10 p-r-25">
              <v-btn
                large
                :loading="isSaving"
                :disabled="isSaving || !isFormValid"
                color="success"
                @click="save">
                {{ text('create_btn') }}
              </v-btn>
            </div>
          </div>
        </v-card>
      </div>

      <!-- Player -->
      <v-bottom-sheet v-model="playerDialog" max-width="960" :persistent="false" inset>
        <v-card tile>
          <iframe id="serviceFrameSend"
                  :src="frameSrc"
                  width="100%"
                  height="380"
                  frameborder="0">
          </iframe>
          <v-list>
            <v-list-tile>
              <v-list-tile-content>
                <v-list-tile-title v-if="form.sourceType === 'channel'">
                  {{ form.channel.name }}
                </v-list-tile-title>
                <v-list-tile-title v-else>
                  {{ form.newSource }}
                </v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
          </v-list>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              depressed
              @click="closePlayer()">
              Close
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-bottom-sheet>

    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { DVRChannelFormToJsonAPI } from '@/store/converters';

export default {
  created() {
    this.getLiveJobs();
    this.setFrameSrc('');
    this.form.duration.hours = this.limit >= 3600 ? 1 : 0;
  },
  data() {
    return {
      channels: [],
      urls: [],
      playerDialog: false,
      frameSrc: '',
      isSaving: false,
      form: {
        id: 0,
        name: '',
        sourceType: 'channel', // channel, newSource
        channel: {},
        channelSource: '',
        newSource: '',
        duration: {
          days: 0,
          hours: 0,
        },
        bitrates: [],
      },
      rules: {
        nameRules: [
          v => !!v || 'Name is required',
          v => (v.length >= 3 && v.length <= 255) || 'Name must be between 3 and 255 characters',
        ],
        sourceRules: [
          v => (v.startsWith('http') && v.includes('.m3u')) || 'Unsupported stream format',
        ],
      },
    };
  },
  computed: {
    loading() {
      return this.$store.getters.isDataLoaded;
    },
    isFormValid() {
      if (!(this.form.name.length >= 3 && this.form.name.length <= 255)) {
        return false;
      }
      if (this.form.sourceType === 'channel' && !this.form.channelSource) {
        return false;
      }
      if (this.form.sourceType === 'newSource' && !this.newSourceIsValid()) {
        return false;
      }
      const dur = Number(this.form.duration.days) * 86400 + Number(this.form.duration.hours) * 3600;
      if (dur === 0) {
        return false;
      }
      if (this.form.sourceType === 'channel' && this.form.bitrates.length === 0) {
        return false;
      }
      return this.isPeriodValid;
    },
    liveJobs() {
      return this.$store.getters['jobs/getLiveJobs'];
    },
    user() {
      return this.$store.getters.user || {};
    },
    limit() {
      let limit = 0;
      if (this.user && this.user.account && this.user.account.limits) {
        limit = this.user.account.limits.dvr_channel_duration_total;
      }
      return limit;
    },
    limitStr() {
      let limit = 0;
      if (this.user && this.user.account && this.user.account.limits) {
        limit = this.user.account.limits.dvr_channel_duration_total;
      }
      const duration = moment.duration(limit * 1000);
      const days = duration.asDays();
      duration.subtract(moment.duration(days, 'days'));
      const hours = duration.hours();
      return `${days}d ${hours}h`;
    },
    isPeriodValid() {
      const dur = Number(this.form.duration.days) * 86400 + Number(this.form.duration.hours) * 3600;
      return dur <= this.limit;
    },
    dict() {
      return this.$store.getters.dictionary;
    },
  },
  methods: {
    getLiveJobs() {
      const args = {
        limit: 500,
        offset: 0,
      };
      this.$store.dispatch('jobs/getLiveJobs', args)
        .then((jobs) => {
          const channels = [];
          for (let i = 0; i < jobs.length; i += 1) {
            const job = jobs[i];
            const outputs = [];
            for (let j = 0; j < job.outputs.length; j += 1) {
              const output = job.outputs[j];
              if (output.format === 'segmented_hls' && output.url.length > 0) {
                outputs.push(this.processURL(output.url[0]));
              }
            }
            const bitrates = [];
            if (job.profile && job.profile.video_template && job.profile.video_template.streams) {
              for (let j = 0; j < job.profile.video_template.streams.length; j += 1) {
                const stream = job.profile.video_template.streams[j];
                bitrates.push({
                  index: j,
                  name: `${stream.bitrate} Kbps`,
                });
              }
            }
            if (outputs.length > 0) {
              channels.push({
                name: job.name,
                urls: outputs,
                bitrates,
              });
            }
          }
          this.channels = channels;
        })
        .catch((errorMsg) => {
          console.log(errorMsg);
        });
    },
    processURL(url) {
      if (url.startsWith('origin')) {
        return url.replace('origin', 'http');
      }
      return url;
    },
    channelSelected() {
      this.urls = this.form.channel.urls;
    },
    save() {
      this.isSaving = true;
      if (!this.isFormValid) return;

      const dc = DVRChannelFormToJsonAPI(this.form);

      const action = 'dvr/createDVRChannel';

      this.$store.dispatch(action, dc)
        .then((result) => {
          this.$router.push({
            name: 'DVRDetailPage',
            params: {
              id: result.id,
            },
          });
        })
        .catch((errorMsg) => {
          this.isSaving = false;
          this.$toasted.error(errorMsg, {
            theme: 'toasted-primary',
            position: 'top-center',
            duration: 7000,
          });
        });
    },
    dayPeriodChanged() {
      if (this.form.duration.days < 0) {
        this.form.duration.days = 0;
      }
    },
    hourPeriodChanged() {
      if (this.form.duration.hours < 0) {
        this.form.duration.hours = 0;
      }
      if (this.form.duration.hours > 23) {
        this.form.duration.hours = 23;
      }
    },
    newSourceIsValid() {
      return !!(this.form.newSource.startsWith('http') && this.form.newSource.includes('.m3u'));
    },
    openPlayer(stream) {
      this.setFrameSrc(stream);
      this.playerDialog = true;
    },
    closePlayer() {
      this.playerDialog = false;
      this.setFrameSrc('');
    },
    setFrameSrc(url) {
      const link = url.replace(/^http:\/\//, 'https://');
      this.frameSrc = `/js/radiant/index.html?src=${link}`;
    },
    text(code) {
      // eslint-disable-next-line no-prototype-builtins
      if (this.dict.hasOwnProperty(code)) {
        return this.dict[code];
      }
      return code;
    },
  },
};
</script>
